import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { WifiOff, Wifi } from "lucide-react";

/**
 * Thin amber banner that stays at the very top of the page while the device
 * reports `navigator.onLine === false`.  When the user reconnects we trigger
 * a TanStack Query invalidate so screens refresh with fresh data.
 */
export default function OfflineBanner() {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [recentlyReconnected, setRecentlyReconnected] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    function onOnline() {
      setOnline(true);
      setRecentlyReconnected(true);
      qc.invalidateQueries();
      const t = setTimeout(() => setRecentlyReconnected(false), 2200);
      return () => clearTimeout(t);
    }
    function onOffline() {
      setOnline(false);
    }
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [qc]);

  if (online && !recentlyReconnected) return null;

  return (
    <div
      className={`fixed inset-x-0 top-0 z-[60] flex items-center justify-center gap-2 text-xs font-medium px-4 py-2 transition-colors ${
        online
          ? "bg-emerald-600 text-white"
          : "bg-amber-500 text-white"
      }`}
      data-testid={online ? "online-banner" : "offline-banner"}
      role="status"
      aria-live="polite"
    >
      {online ? (
        <>
          <Wifi className="w-3.5 h-3.5" />
          <span>Back online — refreshing your data…</span>
        </>
      ) : (
        <>
          <WifiOff className="w-3.5 h-3.5" />
          <span>
            You're offline. Patient data won't refresh until you reconnect.
          </span>
        </>
      )}
    </div>
  );
}
